import React from "react"

import { isTpoFinanceSelection } from "@logic/lib/constants"
import { useQueryClient } from "@tanstack/react-query"
import uniqBy from "lodash/uniqBy"
import { useTranslation } from "react-i18next"
import { useMatch, useNavigate } from "react-router-dom"
import { useSetRecoilState } from "recoil"

import { queryKeys } from "~/api/cache"
import type { ConcertPricingMethodDto } from "~/api/dto/concert.dto"
import { useCreateConcertQuote } from "~/api/hooks/useCreateConcertQuote"
import { useProposalLoanProductPartners } from "~/api/hooks/useProposalLoanProductPartners"
import useUpdateProposalFinanceProduct from "~/api/hooks/useUpdateProposalFinanceProduct"
import Button from "~/components/Button"
import FormFooter from "~/components/FormFooter"
import { SpinnerCircle } from "~/components/Spinner"
import { NumberInput } from "~/components/ui"
import type { IPart } from "~/containers/Proposal/Setup/Form/setup-steps-types"
import { useClearPowurCareWarrantySnapshot } from "~/containers/Proposal/state/designs/extended-warranty-snapshot"
import { useDispatchError } from "~/containers/Proposal/state/notifications/QueryErrorModal"
import { useProposalContext } from "~/containers/Proposal/state/proposal/proposal-context"
import { isProposal } from "~/containers/Proposal/state/proposal/proposal-state"
import { syncWaitingState } from "~/containers/Proposal/state/sync-state"
import {
  LoanProvider,
  useCreateLoanProductMutation,
  useFindLoanProductsForProposalQuery,
} from "~/graphql/client"
import { useGraphqlClient } from "~/hooks/graphql"
import { Dropdown } from "~/ui/lib/options/Select/Dropdown"
import { FinanceType } from "~/utils/finance/constants"

import { ConcertFinancing } from "./ConcertFinancing"
import { GoodleapTpoOffers } from "./GoodleapTpoOffers"
import { LightReachFinancing } from "./LightReachFinancing"
import { DownPaymentInput, StyledForm } from "./styles"
import {
  buildFinanceUpdatePayload,
  useFinancingSelectionSave,
  useFinancingSelectionState,
} from "./useFinancingSelection"
import {
  byTermThenApr,
  makeOptionName,
  shouldSkipFinanceProductStep,
} from "./utils"

export const FinancingSelection: NonNullable<IPart[`form`]> = () => {
  const { t } = useTranslation()
  const onError = useDispatchError()
  const { client } = useGraphqlClient()
  const navigate = useNavigate()
  const routeMatch = useMatch(`proposal/:slug/setup/:stepId/:partId`)
  const { stepId, slug } = routeMatch?.params || {}

  const proposal = useProposalContext()
  const queryClient = useQueryClient()

  const [selectedFinanceType, setSelectedFinanceType] = React.useState<string>(
    () => {
      if (proposal.local.financeType) {
        return proposal.local.financeType
      }

      if (proposal.local.selectedLoanProduct) {
        return proposal.local.selectedLoanProduct.provider
      }

      return FinanceType.CASH
    },
  )

  const selectedTypeIsLoanProvider = Object.values(LoanProvider).includes(
    selectedFinanceType as LoanProvider,
  )
  const providerForLoanProductsQuery =
    selectedFinanceType !== FinanceType.CONCERT && selectedTypeIsLoanProvider
      ? selectedFinanceType
      : FinanceType.CASH

  const query = useFindLoanProductsForProposalQuery(
    client,
    {
      proposalId: proposal.local.id ?? ``,
      provider: providerForLoanProductsQuery,
    },
    {
      enabled: !!proposal.local.id,
      // refetchOnWindowFocus: false,
      // refetchOnReconnect: false,
      // staleTime: Infinity,
      onError,
    },
  )

  const updateProposalFinanceProduct = useUpdateProposalFinanceProduct()
  const createConcertQuote = useCreateConcertQuote()
  const createLoanProduct = useCreateLoanProductMutation(client, { onError })
  const setSyncWaiting = useSetRecoilState(syncWaitingState)
  const clearPowurCareWarrantySnapshot = useClearPowurCareWarrantySnapshot()
  const proposalLoanProductPartnersQuery = useProposalLoanProductPartners(
    proposal.local.id,
  )
  const partnerOptions = proposalLoanProductPartnersQuery.data ?? []
  const partnerOptionByFinanceType = React.useMemo(() => {
    return new Map(partnerOptions.map((option) => [option.financeType, option]))
  }, [partnerOptions])

  const [isSavingNext, setIsSavingNext] = React.useState(false)
  const [selectedConcertPricing, setSelectedConcertPricing] =
    React.useState<ConcertPricingMethodDto | null>(null)

  const loanProducts = uniqBy(
    query.data?.findLoanProductsForProposal.products,
    makeOptionName,
  ).sort(byTermThenApr)

  const {
    disableDecimal,
    handleChangeLightReachProduct,
    handleDownPaymentChange,
    isTpo,
    loanProductOptions,
    saveError,
    selectedDownPayment,
    selectedFinanceProductId,
    selectedFinanceTypeLabel,
    selectedProduct,
    selectedProductGroup,
    selectedUpdatedPricePerWatt,
    setHasUserModifiedFunding,
    setSaveError,
    updateSelected,
  } = useFinancingSelectionState({
    loanProducts,
    partnerOptions,
    proposal,
    selectedFinanceType,
    setSelectedFinanceType,
    t,
  })

  const isCash = selectedFinanceType === FinanceType.CASH
  const isConcert = selectedFinanceType === FinanceType.CONCERT
  const isCreditHuman = selectedFinanceType === FinanceType.CREDIT_HUMAN
  const isLightReach = selectedFinanceType === FinanceType.LIGHT_REACH
  const isGoodleap = selectedFinanceType === FinanceType.GOODLEAP

  const selectedFinanceTypeRequiresFundingGroup =
    partnerOptionByFinanceType.get(selectedFinanceType)?.requiresFundingGroup ??
    false
  const shouldShowNoFinanceProductsMessage =
    shouldSkipFinanceProductStep(selectedFinanceType) && !isConcert
  const selectedFinancePartnerLabel = selectedFinanceTypeLabel
  const showDownPayment =
    !isCash &&
    !isConcert &&
    !isCreditHuman &&
    !isLightReach &&
    selectedFinanceType !== undefined &&
    !isTpo
  const showFinanceProductDropdown =
    !isCash && !isConcert && !isLightReach && selectedFinanceType !== undefined

  const areTpoLoanProductsInList = React.useMemo(
    () =>
      isGoodleap &&
      loanProducts.some(
        (loanProduct) =>
          loanProduct.provider === `goodleap` && loanProduct.loanType === `ppa`,
      ),
    [isGoodleap, loanProducts],
  )

  const activeConcertLoan = React.useMemo(
    () =>
      proposal.local.activeLoans?.find(
        (loan) => loan.provider === LoanProvider.concert,
      ) ?? null,
    [proposal.local.activeLoans],
  )

  const hasActiveConcertLoan = activeConcertLoan !== null

  const financeProductDropdownOptions = React.useMemo(() => {
    const options = loanProductOptions.map((option) => (
      <Dropdown.Item
        label1={option}
        key={option}
        onClick={() => {
          updateSelected([option])
        }}
        isChecked={option === makeOptionName(selectedProduct)}
      />
    ))

    if (query.isLoading) {
      return [
        <Dropdown.Item
          label1={`Loading...`}
          key={`Loading...`}
          onClick={() => undefined}
        />,
        ...options,
      ]
    }

    return options
  }, [loanProductOptions, query.isLoading, selectedProduct, updateSelected])

  const { handleSaveLoanProduct } = useFinancingSelectionSave({
    createLoanProduct,
    onError,
    proposal,
    queryClient,
    selectedDownPayment,
    selectedFinanceProductId,
    selectedProduct,
    selectedUpdatedPricePerWatt,
    setHasUserModifiedFunding,
    setSaveError,
    updateProposalFinanceProduct,
  })

  const hasNonLightReachChanges = React.useMemo(() => {
    const payload = buildFinanceUpdatePayload({
      selectedFinanceType: proposal.local.financeType ?? FinanceType.CASH,
      selectedFundingGroup: proposal.local.fundingGroup ?? null,
      selectedDownPayment,
      selectedFinanceProductId,
      selectedProduct,
      selectedUpdatedPricePerWatt,
    })

    return (
      payload.financeProductId !== (proposal.local.financeProductId ?? null) ||
      payload.selectedLoanProductId !==
        (proposal.local.selectedLoanProduct?.id ?? null) ||
      payload.downPayment !== (proposal.local.downPayment ?? null)
    )
  }, [
    proposal.local.downPayment,
    proposal.local.financeProductId,
    proposal.local.selectedLoanProduct?.id,
    selectedDownPayment,
    selectedFinanceProductId,
    selectedProduct,
    selectedUpdatedPricePerWatt,
  ])

  const navigateToFundingSelection = React.useCallback(() => {
    navigate(`/proposal/${slug}/setup/product-offering/funding-selection`)
  }, [navigate, slug])

  const handleSaveConcertFinancing = React.useCallback(async () => {
    const proposalId = proposal.local.id ?? ``
    if (!proposalId) {
      throw new Error(`Proposal id is required`)
    }

    if (!selectedConcertPricing) {
      setSaveError(`Select a Concert credit quality option before saving`)
      return
    }

    if (
      selectedConcertPricing.term == null ||
      selectedConcertPricing.apr == null ||
      selectedConcertPricing.amountFinanced == null
    ) {
      setSaveError(`Selected Concert pricing option is missing required values`)
      return
    }

    const introTermPayment =
      selectedConcertPricing.calculations?.introTermPayment
    if (introTermPayment == null) {
      setSaveError(
        `Selected Concert pricing option is missing intro term payment`,
      )
      return
    }

    const quoteResponse = await createConcertQuote.mutateAsync({
      proposalId,
      data: {
        term: selectedConcertPricing.term,
        apr: selectedConcertPricing.apr,
        financeProduct: selectedConcertPricing.financingProduct,
        calculations: {
          introTermPayment,
        },
        amountFinanced: selectedConcertPricing.amountFinanced,
      },
    })

    await queryClient.invalidateQueries({
      queryKey: queryKeys.proposal.savings(proposalId),
      refetchType: `active`,
    })

    if (!quoteResponse.loanProductId) {
      setSaveError(`Concert quote did not return a loan product id`)
      return
    }

    await updateProposalFinanceProduct.mutateAsync({
      id: proposalId,
      data: {
        financeType: selectedFinanceType,
        fundingGroup: proposal.local.fundingGroup ?? null,
        selectedLoanProductId: quoteResponse.loanProductId,
        financeProductId: null,
        downPayment: null,
      },
    })

    const refetchResult = await proposal.refetch().catch(() => null)
    const refetchedProposal =
      refetchResult &&
      `data` in refetchResult &&
      isProposal(refetchResult.data?.proposal)
        ? refetchResult.data.proposal
        : null

    proposal.setLocal((curr) => ({
      ...curr,
      selectedLoanProduct: refetchedProposal?.selectedLoanProduct ?? null,
      selectedLoanProductId: quoteResponse.loanProductId,
      downPayment: null,
      financeProductId: null,
    }))

    await queryClient.refetchQueries({
      queryKey: queryKeys.proposal.savings(proposal.local.id),
      type: `active`,
    })

    setSyncWaiting((curr) => ({ ...curr, quotes: true }))
    setHasUserModifiedFunding(false)
    setSaveError(null)
  }, [
    createConcertQuote,
    proposal,
    queryClient,
    selectedConcertPricing,
    selectedFinanceType,
    setHasUserModifiedFunding,
    setSaveError,
    setSyncWaiting,
    updateProposalFinanceProduct,
  ])

  return (
    <StyledForm>
      <section>
        {saveError ? (
          <div
            className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3"
            role="alert"
          >
            <div className="flex items-start gap-2">
              <svg
                className="h-5 w-5 flex-shrink-0 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="text-sm font-medium text-red-900">
                  Funding update failed
                </p>
                <p className="mt-1 text-xs text-red-700">{saveError}</p>
              </div>
            </div>
          </div>
        ) : null}
        {isGoodleap && !areTpoLoanProductsInList && loanProducts.length > 0 && (
          <GoodleapTpoOffers />
        )}
        <main className="flex flex-col gap-4">
          {shouldShowNoFinanceProductsMessage && (
            <div className="w-full rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p>
                {selectedFinancePartnerLabel} is selected as your finance
                partner, and this partner does not have finance products.
              </p>
              <button
                type="button"
                onClick={navigateToFundingSelection}
                className="mt-2 font-semibold text-blue-700 underline hover:text-blue-800"
              >
                Click here to change your finance plan.
              </button>
            </div>
          )}
          {selectedFinanceType === FinanceType.LIGHT_REACH ? (
            <div className="mt-4 w-full">
              <LightReachFinancing
                handleLightReachProductChange={handleChangeLightReachProduct}
              />
            </div>
          ) : isConcert ? (
            <div className="mt-4 w-full">
              <ConcertFinancing
                proposalId={proposal.local.id ?? ``}
                isSaving={proposal.isSaving || isSavingNext}
                onSelectedPricingChange={setSelectedConcertPricing}
                hasActiveConcertLoan={hasActiveConcertLoan}
                proposalQuote={proposal.local.quote}
                selectedLoanProduct={proposal.local.selectedLoanProduct}
                activeConcertLoan={activeConcertLoan}
              />
            </div>
          ) : showFinanceProductDropdown ? (
            <div className="w-full">
              <Dropdown.Container
                Label={() => <label>Finance Product</label>}
                SelectedOption={() => <p>{makeOptionName(selectedProduct)}</p>}
                Options={financeProductDropdownOptions}
              />
            </div>
          ) : null}
        </main>

        <span className="flex flex-col justify-between items-start gap-[30px] min-h-[55px] relative min-[750px]:flex-row min-[750px]:gap-y-0 [&>div]:w-full [&>div]:overflow-hidden">
          {showDownPayment && (
            <DownPaymentInput>
              <p>{t(`downPayment`)}</p>
              <div>
                <NumberInput
                  value={selectedDownPayment}
                  setValue={handleDownPaymentChange}
                  decimalPlaces={0}
                  min={0}
                  testId="down-payment"
                  disabled={disableDecimal}
                />
              </div>
            </DownPaymentInput>
          )}
        </span>
      </section>

      <FormFooter>
        <div className="flex gap-3">
          <Button
            disabled={proposal.isSaving}
            onClick={navigateToFundingSelection}
          >
            Back To Finance Plan
          </Button>
          <Button
            type="button"
            onClick={() => navigate(`/proposal/${slug}/setup/product-offering`)}
          >
            Close
          </Button>
        </div>
        {!(isConcert && hasActiveConcertLoan) && (
          <Button
            type="button"
            onClick={async () => {
              if (isSavingNext) return
              if (!isLightReach && !isConcert && !hasNonLightReachChanges) return

              setIsSavingNext(true)
              try {
                if (isConcert) {
                  await handleSaveConcertFinancing()
                } else {
                  await handleSaveLoanProduct()
                }
                if (
                  isTpoFinanceSelection({
                    financeType: selectedFinanceType,
                    loanType: selectedProduct?.loanType,
                  })
                ) {
                  clearPowurCareWarrantySnapshot()
                }
                setSyncWaiting((curr) => ({ ...curr, design: true }))
                navigate(`/proposal/${slug}/setup/product-offering`)
              } catch (error) {
                onError(error)
              } finally {
                setIsSavingNext(false)
              }
            }}
            disabled={
              proposal.isSaving ||
              isSavingNext ||
              (selectedFinanceTypeRequiresFundingGroup &&
                !selectedProductGroup) ||
              (!isLightReach && !isConcert && !hasNonLightReachChanges)
            }
          >
            <p>{proposal.isSaving || isSavingNext ? t(`saving`) : `Save`}</p>
            {(proposal.isSaving || isSavingNext) && (
              <SpinnerCircle
                size={20}
                backgroundColor="rgba(var(--white-rgb), 0.4)"
                spinnerColor="rgba(var(--white-rgb), 0.8)"
              />
            )}
          </Button>
        )}
      </FormFooter>
    </StyledForm>
  )
}
